// ═══════════════════════════════════════════════════════════
// FROM SURVIVING TO THRIVING — editable content.
// Sections render as calm collapsible cards. `free: true` = open
// to everyone; otherwise the body is locked until the client has
// access ('surviving-to-thriving'). Edit freely.
// ═══════════════════════════════════════════════════════════

export const HERO_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/9cc27ad2-9473-4d8d-951e-838ca155566b/images/22e742907bb5008642ca20ed843d8ca2b57aee6438af1e15ee580d20d2e0a398.jpeg';

export const intro = {
  eyebrow: 'Nervous System Regulation',
  title: 'From Surviving to Thriving',
  tagline: 'How to move out of fight-or-flight and into rest, digest & heal.',
  note: "You can't heal in a body that thinks it's under threat. Before we build, we help the body feel safe. This is where every other kind of wellness becomes possible.",
};

export const sections = [
  {
    id: 'two-gears',
    title: 'Meet Your Two Gears',
    tagline: "What's actually happening in your body.",
    free: true,
    paragraphs: [
      "Your nervous system runs on two main gears. The sympathetic branch is your accelerator — fight, flight or freeze. It floods you with adrenaline and cortisol so you can survive a threat. The parasympathetic branch is your brake — rest, digest and heal. It's where your body repairs tissue, digests food, sleeps deeply and restores.",
      "Neither gear is 'bad'. You need both. Health isn't staying calm all the time — it's flexibility: revving up when life asks for it, then coming back down to heal.",
      "The trouble is that modern life keeps many of us with a foot on the accelerator — a low-grade survival mode, day after day. And deep healing simply cannot happen there.",
    ],
    list: [
      { title: 'Survival gear (sympathetic)', text: 'Fight · flight · freeze. Fast heart, shallow breath, tense muscles, alert mind.' },
      { title: 'Healing gear (parasympathetic)', text: 'Rest · digest · heal. Slow breath, soft belly, deep sleep, repair & digestion.' },
      { title: 'The vagus nerve', text: 'The great wandering nerve linking brain, heart and gut — your main pathway home to calm.' },
    ],
  },
  {
    id: 'signs',
    title: "Signs You're Stuck in Survival",
    tagline: "A mirror, not a diagnosis.",
    free: true,
    intro: 'See how many feel familiar. The more that land, the more your system may be idling in survival mode.',
    list: [
      'Wired but tired — exhausted, yet unable to switch off',
      'A racing mind; trouble settling or falling asleep',
      'Shallow, high-chest breathing',
      'Poor digestion, bloating, unsettled gut',
      'Tight jaw, neck and shoulders',
      'Anxiety, irritability, a feeling of being on edge',
      'Cravings for sugar, salt or caffeine',
      'Feeling numb, foggy or shut down (the freeze response)',
    ],
    links: [
      { label: 'Check in with the Wellness Wheel', to: '/programs/terrain/wellness-wheel' },
    ],
  },
  {
    id: 'why-stuck',
    title: 'Why We Get Stuck',
    tagline: 'Your body is protecting you, not betraying you.',
    free: false,
    paragraphs: [
      "We get stuck for good reasons: chronic stress with no true 'off' switch, experiences the body never got to complete or process, relentless over-doing, blood-sugar swings, stimulants, and too few genuine cues of safety.",
      "The body keeps score. It learns that the world is unsafe and stays braced — ready, tense, alert. This isn't weakness or failure. It's a brilliant, protective adaptation that has simply outstayed its usefulness.",
      "The good news: what was learned can be unlearned. A nervous system that learned survival can be gently retrained toward safety.",
    ],
    links: [
      { label: 'Read: A Life Journey — Out of Crisis', to: '/resources/whole-self?resource=life-journey-out-of-crisis' },
    ],
  },
  {
    id: 'the-shift',
    title: 'The Shift: From Survival to Safety',
    tagline: 'The goal is flexibility, not the absence of stress.',
    free: false,
    paragraphs: [
      "We don't heal by eliminating stress — that's impossible, and it isn't the point. We heal by restoring the body's ability to shift gears: to activate when needed, then return to calm and repair.",
      "Beneath your awareness, your body is constantly scanning: am I safe, or in danger? (This is called neuroception.) We can't think our way to safety — we have to show the body, again and again, through the senses.",
      "That's why this work is bottom-up (body-first), not just top-down (thinking). Breath, movement, warmth, rhythm and connection speak the body's language directly.",
    ],
    links: [
      { label: 'Play the Stabilize territory in The Life Ninja Game', to: '/programs/life-ninja-game' },
    ],
  },
  {
    id: 'daily-practices',
    title: 'Daily Practices to Downshift',
    tagline: 'Small, repeatable cues of safety.',
    free: false,
    intro: "You don't need hours. You need small, frequent signals that tell your body it's safe to come off the accelerator.",
    list: [
      { title: 'Extend your exhale', text: 'Breathe in for 4, out for 6–8. Or try a physiological sigh: two inhales through the nose, one long sigh out.' },
      { title: 'Ground & orient', text: 'Feet on the earth. Slowly look around the room. Tell your body: I am here. I am safe. Right now, in this moment.' },
      { title: 'Warm & cool', text: 'A warm belly softens; a cool splash on the face gently dives the vagus nerve toward calm.' },
      { title: 'Move & release fascia', text: 'Gentle movement discharges the survival energy stored in the body.' },
      { title: 'Hydrate & mineralize', text: 'Dehydration and low minerals keep the body on edge. Clean water plus minerals help you settle.' },
      { title: 'Rhythm & light', text: 'Morning light, regular meals and steady sleep times tell the body the world is predictable — and therefore safe.' },
      { title: 'Voice & connection', text: 'Humming, singing and safe social time tone the vagus nerve and soothe the system.' },
    ],
    links: [
      { label: 'The 28 Day Stress Reset', to: '/programs/fascia-reset' },
      { label: 'Discover fascia & the lines', to: '/learn/fascia' },
    ],
  },
  {
    id: 'retrain',
    title: 'Retrain the Nervous System',
    tagline: 'Consistency over intensity.',
    free: false,
    paragraphs: [
      "Retraining isn't about one big breakthrough — it's repetition until safety becomes your body's default. Small doses, most days, gently build capacity (this is called titration).",
      "Don't flood a fragile system. Prioritise sleep and nourishment, go at the pace your body can hold, and let each level rest on the one before it.",
    ],
    list: [
      'Leave the sea — recognise you\u2019re in crisis',
      'Find the shore — reach solid ground',
      'Stabilize — regulate before you build',
      'Retrain — repeat cues of safety',
      'Observe — watch without judgement',
      'Find neutrality — let the charge settle',
      'Release — let go of what\u2019s held',
    ],
    links: [
      { label: 'The full map: A Life Journey — Out of Crisis', to: '/resources/whole-self?resource=life-journey-out-of-crisis' },
    ],
  },
  {
    id: 'thriving',
    title: 'From Surviving to Thriving',
    tagline: 'Capacity, resilience & becoming.',
    free: false,
    paragraphs: [
      "Thriving isn't the absence of stress — it's capacity and resilience. You can meet life, get activated, and reliably return to calm. Digestion works. Sleep restores. Mood steadies. Creativity, connection and purpose come back online.",
      "This is where real growth lives. Once the body trusts it is safe, you're no longer surviving your life — you're building it.",
    ],
    links: [
      { label: 'Journey onward with The Life Ninja Game', to: '/programs/life-ninja-game' },
      { label: 'Go deeper with VIP Coaching & the Four Pillars', to: '/programs/vip/coaching' },
    ],
  },
];
